import React from 'react';
import { get, noop } from 'lodash';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { authFlowCallerIdentificationPharmacySearch as AuthFlowCallerIdentificationPharmacySearchView } from 'ui/views/widgets/authFlowCallerIdentificationPharmacySearch';
import { AuthFlowCallerIdentificationPharmacySearchService } from 'services/authFlowCallerIdentificationPharmacySearch';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { isInbound, getCallerTypeFromName } from 'resolvers/callStateEntities';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowCallerIdentificationPharmacySearchWidget } from 'ui/containers/AuthFlowCallerIdentificationPharmacySearchWidget';
 *
 * // render
 * <AuthFlowCallerIdentificationPharmacySearchWidget propName='propValue'/>
 */
class _AuthFlowCallerIdentificationPharmacySearchWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            requestPayload: {}
        };

        // create service instance
        this.authFlowCallerIdentificationPharmacySearchService = new AuthFlowCallerIdentificationPharmacySearchService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowCallerIdentificationPharmacySearchWidget.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationPharmacySearchService.cancel();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationPharmacySearchService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // get callType from store
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        // set requestPayload for table widget
        const requestPayload = {
            context: {
                module: module
            }
        };

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                requestPayload: requestPayload
            } );
        }
    }

    /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {} } = {} ) {
        log.debug( 'AuthFlowCallerIdentificationPharmacySearchWidget.handleActionClick()' );

        // get member id
        const pharmacyId = get( item, 'id', null );
        const firstName = get( item, 'values.firstName', '' );
        const lastName = get( item, 'values.lastName', '' );

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.callerType );

        /*---------*/

        // when `initiateCall` group action is clicked, initiate outbound call
        if( 'groupAction' === type && 'initiateCall' === value.name ) {
            return this.initiateOutboundCall( {
                actionName: value.name,
                url: value.url,
                payload: {
                    id: pharmacyId,
                    firstName,
                    lastName,
                },
                callerType: callerType
            } );
        }
    }

    /**
     * @desc Request backend to start an outbound call for member.
     */
    async initiateOutboundCall( { actionName, url, payload, callerType } ) {
        log.debug( 'AuthFlowCallerIdentificationPharmacySearchWidget.initiateOutboundCall()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationPharmacySearchService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationPharmacySearchService.initiateOutboundCall( {
            url: url,
            data: payload,
            callerType: callerType
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {
                this.props.initCallState( data.callData ); // save call data in the store
                this.props.onNextActionClick( { name: actionName } ); // move to the next step
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.authFlowCallerIdentificationPharmacySearchService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        
        return (
            <AuthFlowCallerIdentificationPharmacySearchView
                className={ this.props.className }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={ this.handleActionClick }
                requestPayload= { this.state.requestPayload }
            />
        );
    }
}

// set display name
_AuthFlowCallerIdentificationPharmacySearchWidget.displayName = '_AuthFlowCallerIdentificationPharmacySearchWidget';

// set default props
_AuthFlowCallerIdentificationPharmacySearchWidget.defaultProps = {
    widget: {},
    onNextActionClick: noop,
};

// prop types
_AuthFlowCallerIdentificationPharmacySearchWidget.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const AuthFlowCallerIdentificationPharmacySearchWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowCallerIdentificationPharmacySearchWidget ) );
